import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Mail, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

export const EmailCodeModal = ({ isOpen, onClose, onVerify, email, isLoading, error }) => {
  const [code, setCode] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length === 6) {
      // Sanitize: Nur Ziffern, getrimmt, exakt 6 Zeichen
      const sanitizedCode = code.replace(/\D/g, '').slice(0, 6);
      onVerify(sanitizedCode);
    }
  };

  const formRef = React.useRef(null);

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    if (value.length === 6) {
      setTimeout(() => formRef.current?.requestSubmit(), 50);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-md bg-zinc-950 border-emerald-500/20"
        data-testid="email-code-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <Mail className="w-5 h-5" />
            Sicherheitscode eingeben
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Ein 6-stelliger Code wurde an <strong className="text-zinc-200">{email}</strong> gesendet.
            <br />
            Der Code ist 5 Minuten gültig.
          </DialogDescription>
        </DialogHeader>

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <Alert variant="destructive" className="bg-red-950/30 border-red-500/50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription data-testid="error-message">{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Input
              data-testid="code-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="000000"
              value={code}
              onChange={handleCodeChange}
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono bg-zinc-900 border-zinc-700 focus:border-emerald-500 text-zinc-100"
              autoFocus
              disabled={isLoading}
            />
            <p className="text-xs text-zinc-500 text-center">
              {code.length}/6 Ziffern
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-zinc-700 hover:bg-zinc-800"
              disabled={isLoading}
              data-testid="cancel-button"
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              disabled={code.length !== 6 || isLoading}
              data-testid="verify-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifiziere...
                </>
              ) : (
                'Code bestätigen'
              )}
            </Button>
          </div>
        </form>

        <div className="mt-4 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
          <p className="text-xs text-zinc-500">
            💡 <strong>Tipp:</strong> Prüfe deinen Spam-Ordner, falls die E-Mail nicht ankommt.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
